import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Scale } from "lucide-react";
import { toast } from "sonner";

export default function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [resolution, setResolution] = useState("");
  const load = useCallback(() => api.get("/admin/disputes").then((r) => setDisputes(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, status) => {
    await api.put(`/admin/disputes/${id}`, { resolution: resolution || (status === "resolved" ? "Resolved in favor of host" : "Dispute rejected"), status });
    toast.success(`Dispute ${status}`);
    setResolution("");
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-disputes">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Disputes</h1>
        <p className="text-muted-foreground text-sm mt-1">Review and resolve open disputes.</p>
      </div>
      {disputes.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          <Scale className="w-8 h-8 mx-auto mb-3 opacity-40" /> No disputes.
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((d) => (
            <div key={d.id} className="border border-border rounded-md bg-white p-5" data-testid={`dispute-${d.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-sapphire">{d.job?.property_id ? "Job Dispute" : "Dispute"}</div>
                  <div className="text-sm text-muted-foreground">Host: {d.pm?.name} · Cleaner: {d.cleaner?.name || "—"}</div>
                  <p className="text-sm text-slate-700 mt-2"><span className="font-semibold">Reason:</span> {d.reason}</p>
                  {d.resolution && <p className="text-sm text-emerald-700 mt-1"><span className="font-semibold">Resolution:</span> {d.resolution}</p>}
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${d.status === "open" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>{d.status}</span>
              </div>
              {d.status === "open" && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-ocean hover:bg-ocean/90 mt-3" size="sm" data-testid={`resolve-${d.id}`}>Resolve</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white">
                    <DialogHeader><DialogTitle className="font-heading">Resolve Dispute</DialogTitle></DialogHeader>
                    <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Resolution notes…" data-testid="resolution-input" />
                    <DialogFooter className="gap-2">
                      <Button variant="outline" onClick={() => resolve(d.id, "rejected")} data-testid={`reject-${d.id}`}>Reject</Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => resolve(d.id, "resolved")} data-testid={`confirm-resolve-${d.id}`}>Mark Resolved</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
